// =====================================================================
// Chamados — a PODA dos filtros ao trocar de aba.
//
// ⚠️⚠️ NÃO É ESPELHO DE NADA no backend (ao contrário do `chamados-fila-utils.js`,
//    o vizinho de pasta). Isto é regra de TELA: os filtros da Consulta são todos
//    aplicados no cliente, sobre a lista que já veio inteira.
//
// ---------------------------------------------------------------------
// O BUG QUE ISTO CONSERTA (relatado pela Julia em 2026-08-27)
// ---------------------------------------------------------------------
// As abas (*Em aberto* · *Encerrados* · *Todos*) recarregam a lista, mas os
// filtros ficavam como estavam. Filtrar por situação "Em atendimento" na aba
// *Em aberto* e trocar para *Encerrados* dava uma tela VAZIA — porque nenhum
// chamado encerrado está "em atendimento". A pessoa vê zero linhas e conclui que
// não há registro; só depois nota o filtro e limpa na mão.
//
// ⚠️⚠️ E LIMPAR TUDO TAMBÉM ESTARIA ERRADO, que é a correção óbvia e preguiçosa.
//    O gerente que filtrou pela loja dele quer continuar vendo a loja dele nas
//    três abas — zerar `loja` a cada clique transformaria um incômodo raro
//    (filtro impossível) num incômodo constante (refazer o filtro toda vez).
//    Por isso a poda é CIRÚRGICA: sai só o que não tem mais como casar.
// ---------------------------------------------------------------------

/**
 * Devolve os filtros com os valores IMPOSSÍVEIS na lista nova zerados.
 *
 * "Impossível" = nenhum chamado da lista nova casaria com aquele valor. Note que
 * o critério é por CAMPO, isolado: um filtro que sozinho ainda acha algo é
 * mantido, mesmo que a combinação de todos resulte em zero linhas.
 *
 * ⚠️ E isso é decisão, não descuido. Combinação vazia é uma pergunta legítima
 *    ("tem chamado encerrado da MAL em julho?" — não tem), e a resposta certa é
 *    a lista vazia COM os filtros à vista. O que confunde é o filtro que não
 *    pode casar nunca naquela aba, e é só ele que sai.
 *
 * ⚠️ LISTA VAZIA NÃO PODA NADA. Aba sem nenhum chamado tornaria "impossível"
 *    todo filtro, e a poda apagaria a escolha da pessoa por causa de uma aba
 *    vazia — inclusive numa falha de carregamento, em que `chamados` é `[]`.
 *
 * @param {object} filtros  { loja, tipo, prioridade, status, periodo }
 * @param {object[]} chamados  a lista JÁ CARREGADA da aba nova
 * @param {(c: object) => string} mesDe  extrai 'aaaa-mm' do chamado (a Consulta
 *        calcula em hora local, e a poda tem de usar exatamente o mesmo critério
 *        da filtragem — senão poda o que casaria, ou mantém o que não casa)
 * @returns {object} os filtros podados (o MESMO objeto se nada mudou)
 */
export function podarFiltros(filtros, chamados, mesDe) {
    if (!chamados || !chamados.length) return filtros;

    // Cada entrada responde: "este valor ainda casa com ALGUM chamado da lista?"
    const aindaCasa = {
        loja: (v) => chamados.some((c) => c.LOJA === v),
        tipo: (v) => chamados.some((c) => c.TIPO_SOLICITACAO === v),
        // ⚠️ 'sem' é valor de verdade — chamado não triado NÃO tem prioridade, e
        //    "sem prioridade" é justamente a fila do Gerente TI. Tratá-lo como
        //    texto comum o podaria sempre (nenhum chamado tem PRIORIDADE = 'sem').
        prioridade: (v) => (v === 'sem'
            ? chamados.some((c) => !c.PRIORIDADE)
            : chamados.some((c) => c.PRIORIDADE === v)),
        status: (v) => chamados.some((c) => c.STATUS === v),
        periodo: (v) => chamados.some((c) => mesDe(c) === v),
    };

    let mudou = false;
    const podados = { ...filtros };
    for (const campo of Object.keys(aindaCasa)) {
        const valor = filtros[campo];
        if (!valor) continue;                      // já vazio: nada a podar
        if (aindaCasa[campo](valor)) continue;     // ainda casa: fica
        podados[campo] = '';
        mudou = true;
    }
    // ⚠️ Devolve o objeto ORIGINAL quando nada mudou: em `setFiltros` isso evita
    //    um re-render por troca de aba sem nenhuma diferença de conteúdo.
    return mudou ? podados : filtros;
}

export default podarFiltros;
